/**
 * 시나리오 — 모델·커플링 설정과 분리된 실행 입력: «어느 참가자의 어느 입력을, 언제(sim 시각 또는
 * 같은 참가자의 변수 조건), 어떻게(set·ramp·pulse) 바꾼다» 의 목록. 프로젝트가 소유한다(`ProjectData.scenarios`).
 *
 * 실행 때 웹이 참가자별 시나리오 파일(`inputs/<n>/scenario.yaml`)으로 잘라 쓰고 어댑터 인자(`--scenario`)로 넘기면, 어댑터가
 * 스텝 경계에서 적용한다. 꼴과 규칙의 정본은 adapter-core `adapter_core.scenario`(vsmr-common) — 여기 등록표
 * (동작·비교 연산자·판 번호)는 그 꼴을 그대로 옮긴 것이고, 이 모듈의 검사는 어댑터의 적재 검사와 같은 규칙이다
 * (어댑터는 실행에서 한 번 더 검사한다 — 웹이 막는 것이 먼저 보인다).
 *
 * `input`·`when.variable` 의 뜻은 참가자 종류가 정한다(종류 스펙 `authoring.loadScenarioCatalog`):
 * FMU 는 변수 이름(승격 입력 − 결선 입력 / 승격 입·출력), MARS 는 ICV 선언 `<종류>:<대상>` / minor edit 요청
 * `카드:변수:파라미터`.
 */

/** 시나리오 파일의 판 — adapter-core `SCHEMA_VERSION` 과 같아야 한다 */
export const SCENARIO_SCHEMA_VERSION = 1 as const;

/** 동작의 종류 — adapter-core `OPS` */
export const SCENARIO_OPS = ['set', 'ramp', 'pulse'] as const;
export type ScenarioOp = (typeof SCENARIO_OPS)[number];

/** 조건의 비교 연산자 — adapter-core `CMPS` */
export const SCENARIO_CMPS = ['<', '<=', '>', '>='] as const;
export type ScenarioCmp = (typeof SCENARIO_CMPS)[number];

export interface ScenarioWhen {
  /** 같은 참가자의 관측 가능 변수 */
  variable: string;
  cmp: ScenarioCmp;
  threshold: number;
  /** 이 sim 시각 전에는 평가하지 않는다(기본 0) — 시작부터 참인 조건을 거를 때 */
  after?: number;
}

export interface ScenarioAction {
  /** 절대 sim 초 — `when` 과 둘 중 하나 */
  at?: number;
  when?: ScenarioWhen;
  /** 참가자(프로젝트의 모델) id — 이름을 바꿔도 깨지지 않는다 */
  modelId: string;
  /** 참가자 종류가 뜻을 정하는 입력 이름 */
  input: string;
  op: ScenarioOp;
  /** set·pulse 의 값, ramp 의 목표값. trip 은 참/거짓 */
  value: number | boolean;
  /** ramp 전용, 초 > 0 */
  duration?: number;
  /** pulse 전용, 초 > 0 */
  width?: number;
  note?: string;
}

export interface Scenario {
  id: string;
  name: string;
  description?: string;
  actions: ScenarioAction[];
}

/** 참가자 하나의 조작 가능 입력·관측 가능 변수 — 종류 스펙이 모델에서 유도한다 */
export interface ScenarioCatalog {
  inputs: ReadonlySet<string>;
  variables: ReadonlySet<string>;
}

export interface ScenarioProblem {
  /** 동작의 순번(0 부터) — 시나리오 수준 문제는 undefined */
  index?: number;
  message: string;
}

const isFinite_ = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);

/**
 * 동작 하나의 꼴 검사 — adapter-core `_parse_action` 과 같은 규칙. `catalog` 가 있으면 이름(입력·변수)까지 본다.
 * 모델 존재는 `validateScenario` 가 본다(동작은 모델 목록을 모른다).
 */
export function validateScenarioAction(a: ScenarioAction, catalog?: ScenarioCatalog): string[] {
  const errs: string[] = [];
  const hasAt = a.at !== undefined;
  const hasWhen = a.when !== undefined;
  if (hasAt === hasWhen) errs.push('at 과 when 중 하나만 있어야 합니다');
  if (hasAt && !(isFinite_(a.at) && a.at >= 0)) errs.push(`at 은 0 이상의 sim 초여야 합니다: ${String(a.at)}`);
  if (hasWhen) {
    const w = a.when!;
    if (!w.variable) errs.push('when.variable 이 없습니다');
    else if (catalog && !catalog.variables.has(w.variable)) errs.push(`when.variable «${w.variable}» 은 이 참가자의 관측 가능 목록에 없습니다`);
    if (!(SCENARIO_CMPS as readonly string[]).includes(w.cmp)) errs.push(`when.cmp 는 ${SCENARIO_CMPS.join('/')} 중 하나여야 합니다: ${String(w.cmp)}`);
    if (!isFinite_(w.threshold)) errs.push(`when.threshold 는 수여야 합니다: ${String(w.threshold)}`);
    if (w.after !== undefined && !(isFinite_(w.after) && w.after >= 0)) errs.push(`when.after 는 0 이상의 sim 초여야 합니다: ${String(w.after)}`);
  }
  if (!a.input) errs.push('input 이 없습니다');
  else if (catalog && !catalog.inputs.has(a.input)) errs.push(`input «${a.input}» 은 이 참가자의 조작 가능 목록에 없습니다`);
  if (!(SCENARIO_OPS as readonly string[]).includes(a.op)) errs.push(`op 는 ${SCENARIO_OPS.join('/')} 중 하나여야 합니다: ${String(a.op)}`);
  if (!(typeof a.value === 'boolean' || isFinite_(a.value))) errs.push(`value 는 수나 참/거짓이어야 합니다: ${String(a.value)}`);
  else if (a.op === 'ramp' && typeof a.value === 'boolean') errs.push('ramp 의 value 는 수여야 합니다');
  if (a.op === 'ramp') {
    if (!(isFinite_(a.duration) && a.duration > 0)) errs.push(`ramp 는 duration(초 > 0)이 있어야 합니다: ${String(a.duration)}`);
    if (a.width !== undefined) errs.push('ramp 에는 width 를 쓰지 않습니다');
  } else if (a.op === 'pulse') {
    if (!(isFinite_(a.width) && a.width > 0)) errs.push(`pulse 는 width(초 > 0)가 있어야 합니다: ${String(a.width)}`);
    if (a.duration !== undefined) errs.push('pulse 에는 duration 을 쓰지 않습니다');
  } else if (a.op === 'set' && (a.duration !== undefined || a.width !== undefined)) {
    errs.push('set 에는 duration·width 를 쓰지 않습니다');
  }
  return errs;
}

/**
 * 시나리오 전체 검사 — 동작마다 꼴과(카탈로그가 있으면) 이름, 그리고 모델 존재. `catalogs` 는 modelId → 카탈로그
 * (모르는 모델은 이름 검사를 건너뛴다 — 시작 때는 참가자 전원의 카탈로그가 있다).
 * `maxTime` 이 있으면 `at`(또는 `when.after`)이 실행 창을 넘는 동작을 막는다.
 */
export function validateScenario(
  scenario: Scenario,
  modelIds: ReadonlySet<string>,
  catalogs?: ReadonlyMap<string, ScenarioCatalog>,
  maxTime?: number,
): ScenarioProblem[] {
  const out: ScenarioProblem[] = [];
  if (!scenario.id) out.push({ message: '시나리오 id 가 없습니다' });
  if (!scenario.name?.trim()) out.push({ message: '시나리오 이름이 없습니다' });
  scenario.actions.forEach((a, index) => {
    if (!modelIds.has(a.modelId)) out.push({ index, message: `대상 모델(${a.modelId})이 프로젝트에 없습니다` });
    for (const message of validateScenarioAction(a, catalogs?.get(a.modelId))) out.push({ index, message });
    const dueFrom = a.at ?? a.when?.after ?? 0;
    if (maxTime !== undefined && isFinite_(dueFrom) && dueFrom > maxTime) {
      out.push({ index, message: `발동 시각 ${dueFrom} s 가 실행 창(${maxTime} s)을 넘습니다` });
    }
  });
  return out;
}

export function describeScenarioAction(a: ScenarioAction): string {
  const when = a.at !== undefined
    ? `at ${a.at}`
    : `when ${a.when?.variable} ${a.when?.cmp} ${a.when?.threshold}${a.when?.after ? ` after ${a.when.after}` : ''}`;
  const extra = a.op === 'ramp' ? ` over ${a.duration}s` : a.op === 'pulse' ? ` for ${a.width}s` : '';
  return `${when}: ${a.op} ${a.input} = ${String(a.value)}${extra}`;
}
